import React, { useState } from "react";
import { C } from "../../../core/constants";
import { resolveCoupleMatchLine } from "../coupleMatchNarrative";
import {
  animalEmoji,
  chartSideLabel,
  elementColor,
  elementLabel,
  elementTierColor,
  formatAnimal,
} from "../coupleMatchVisuals";
import {
  CollapsibleGuide,
  InsightList,
  LegendRow,
  MatrixCellPopover,
  SectionIntro,
  YearDetailPanel,
} from "./CoupleMatchExplainer";

const ELEMENTS = ["Wood", "Fire", "Earth", "Metal", "Water"];

export function OverallScoreRing({ score, band, acc, t }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const bandLabel = t(`couple_match_band_${band}`);

  return (
    <div style={{ textAlign: "center" }}>
      <svg width={140} height={140} viewBox="0 0 140 140" aria-hidden="true">
        <circle cx={70} cy={70} r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={10} />
        <circle
          cx={70}
          cy={70}
          r={radius}
          fill="none"
          stroke={acc.secondary}
          strokeWidth={10}
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          transform="rotate(-90 70 70)"
          style={{ filter: `drop-shadow(0 0 8px ${acc.secondary}88)` }}
        />
        <text x={70} y={66} textAnchor="middle" fill={C.textPrimary} fontSize={28} fontFamily="'Cormorant Garamond', serif">
          {score}
        </text>
        <text x={70} y={86} textAnchor="middle" fill={C.textMuted} fontSize={11}>
          /100
        </text>
      </svg>
      <div style={{ marginTop: 8, fontSize: 12, letterSpacing: "0.12em", color: acc.secondary, textTransform: "uppercase" }}>
        {bandLabel}
      </div>
    </div>
  );
}

export function DimensionBars({ dimensions, acc, t }) {
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {dimensions.map((dim) => (
        <div key={dim.key}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
            <span style={{ color: C.textPrimary }}>{t(`couple_match_dim_${dim.key}`)}</span>
            <span style={{ color: acc.primary }}>{dim.score}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
            <div
              style={{
                width: `${dim.score}%`,
                height: "100%",
                borderRadius: 999,
                background: `linear-gradient(90deg, ${acc.primary}aa, ${acc.secondary})`,
                boxShadow: `0 0 10px ${acc.primary}55`,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function PillarCard({ pillar, acc, role, isDayPillar, t, locale }) {
  const elStyle = elementColor(pillar.element);
  return (
    <div
      style={{
        padding: "12px 10px",
        borderRadius: 10,
        background: "rgba(255,255,255,0.04)",
        border: `1px solid ${isDayPillar ? `${acc.secondary}55` : `${acc.primary}22`}`,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 10, color: C.textMuted, letterSpacing: "0.08em", marginBottom: 4 }}>
        {pillar.key.toUpperCase()}
      </div>
      {isDayPillar && (
        <div style={{ fontSize: 9, color: acc.secondary, marginBottom: 4, letterSpacing: "0.06em" }}>
          {t("couple_match_pillar_day_badge")}
        </div>
      )}
      <div style={{ fontSize: 20, color: acc.secondary, marginBottom: 4 }}>{pillar.ganZhi}</div>
      <div style={{ fontSize: 13, color: C.textSecondary }}>
        {formatAnimal(pillar.animal, locale)}
      </div>
      <div
        style={{
          fontSize: 10,
          color: elStyle.color,
          marginTop: 6,
          padding: "2px 8px",
          borderRadius: 999,
          display: "inline-block",
          background: elStyle.glow,
          border: `1px solid ${elStyle.color}44`,
        }}
      >
        {elStyle.symbol} {elementLabel(pillar.element, t)}
      </div>
      {role && (
        <div style={{ fontSize: 10, color: C.textMuted, marginTop: 8, lineHeight: 1.45 }}>{role}</div>
      )}
    </div>
  );
}

export function DualPillarCards({ partners, narratives, acc, t, locale = "en", isMobile = false }) {
  const comparisons = narratives?.pillars?.comparisons || [];

  return (
    <div style={{ minWidth: 0 }}>
      <SectionIntro>{narratives?.pillars?.intro}</SectionIntro>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
          gap: 20,
          minWidth: 0,
        }}
      >
        {["a", "b"].map((side) => {
          const partner = partners[side];
          const title = chartSideLabel(partners, side, t);
          return (
            <div key={side} style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: acc.secondary, letterSpacing: "0.1em", marginBottom: 10 }}>
                {title}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
                  gap: 10,
                  minWidth: 0,
                }}
              >
                {partner.pillars.map((pillar) => (
                  <PillarCard
                    key={`${side}-${pillar.key}`}
                    pillar={pillar}
                    acc={acc}
                    role={comparisons.find((c) => c.key === pillar.key)?.roleA}
                    isDayPillar={pillar.key === "day"}
                    t={t}
                    locale={locale}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {comparisons.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.1em", marginBottom: 10 }}>
            {t("couple_match_pillars_compare_title")}
          </div>
          <div style={{ display: "grid", gap: 10 }}>
            {comparisons.map((row) => (
              <div
                key={row.key}
                style={{
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.03)",
                  border: row.key === "day" ? `1px solid ${acc.secondary}33` : "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, color: C.textPrimary, textTransform: "capitalize" }}>
                    {row.key} · {formatAnimal(row.partnerA?.animal, locale)} ({elementLabel(row.partnerA?.element, t)}) ↔{" "}
                    {formatAnimal(row.partnerB?.animal, locale)} ({elementLabel(row.partnerB?.element, t)})
                  </span>
                  {row.branchLabel && (
                    <span
                      style={{
                        fontSize: 10,
                        color: acc.secondary,
                        padding: "2px 8px",
                        borderRadius: 999,
                        border: `1px solid ${acc.secondary}44`,
                        letterSpacing: "0.04em",
                      }}
                    >
                      {row.branchLabel}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.6 }}>{row.insightText}</div>
                {row.elementText && (
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 6, lineHeight: 1.55 }}>{row.elementText}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PillarMatrixHeatmap({ matrix, narratives, acc, t, isMobile = false, locale = "en" }) {
  const keys = ["year", "month", "day", "hour"];
  const cells = narratives?.matrix?.cells || matrix || [];
  const cellMap = (aKey, bKey) => cells.find((m) => m.aKey === aKey && m.bKey === bKey);
  const [selected, setSelected] = useState(null);

  const colorFor = (cell) => {
    const band = cell?.band || cell?.severity;
    if (band === "harmony") return `${acc.secondary}33`;
    if (band === "caution" || band === "clash") return "rgba(255,100,120,0.22)";
    return "rgba(255,255,255,0.05)";
  };

  const labelWidth = isMobile ? 52 : 64;
  const legendItems = [
    { id: "h", label: t("couple_match_matrix_band_harmony"), color: acc.secondary },
    { id: "n", label: t("couple_match_matrix_band_neutral"), color: C.textMuted },
    { id: "c", label: t("couple_match_matrix_band_caution"), color: "#ff7b9c" },
  ];

  return (
    <div style={{ minWidth: 0, maxWidth: "100%" }}>
      <SectionIntro>{narratives?.matrix?.intro}</SectionIntro>
      <LegendRow items={legendItems} acc={acc} />
      <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>{narratives?.matrix?.rowsLabel}</div>
      <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 10 }}>{narratives?.matrix?.colsLabel}</div>
      {narratives?.matrix?.summary && (
        <p style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.6, marginBottom: 12 }}>{narratives.matrix.summary}</p>
      )}
      <CollapsibleGuide title={t("couple_match_matrix_guide_title")}>
        <div>{t("couple_match_matrix_band_harmony")}</div>
        <div>{t("couple_match_matrix_band_neutral")}</div>
        <div>{t("couple_match_matrix_band_caution")}</div>
      </CollapsibleGuide>
      <div style={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `${labelWidth}px repeat(${keys.length}, minmax(44px, 1fr))`,
            gap: 6,
            fontSize: isMobile ? 10 : 11,
            minWidth: 0,
          }}
        >
          <div />
          {keys.map((k) => (
            <div key={`h-${k}`} style={{ color: C.textMuted, textAlign: "center", textTransform: "capitalize" }}>
              {k}
            </div>
          ))}
          {keys.map((aKey) => (
            <React.Fragment key={`row-${aKey}`}>
              <div style={{ color: C.textMuted, display: "flex", alignItems: "center", textTransform: "capitalize" }}>{aKey}</div>
              {keys.map((bKey) => {
                const item = cellMap(aKey, bKey);
                const isSelected = selected?.aKey === aKey && selected?.bKey === bKey;
                return (
                  <button
                    key={`${aKey}-${bKey}`}
                    type="button"
                    onClick={() => setSelected(isSelected ? null : item)}
                    style={{
                      borderRadius: 8,
                      padding: "10px 4px",
                      textAlign: "center",
                      background: colorFor(item),
                      color: C.textPrimary,
                      border: isSelected ? `1px solid ${acc.secondary}` : "1px solid rgba(255,255,255,0.06)",
                      cursor: "pointer",
                      font: "inherit",
                    }}
                  >
                    {item?.score ?? "—"}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      <MatrixCellPopover cell={selected} acc={acc} locale={locale} onClose={() => setSelected(null)} />
    </div>
  );
}

export function ElementBalanceCompare({ partners, narratives, acc, t, locale = "en", isMobile = false }) {
  const combined = narratives?.elements?.combined || {};
  const maxCombined = Math.max(...ELEMENTS.map((el) => combined[el] || 0), 1);

  const renderBar = (el, count, maxVal) => {
    const style = elementColor(el);
    const width = maxVal > 0 ? Math.min(100, (count / maxVal) * 100) : 0;
    return (
      <div key={el} style={{ marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textSecondary }}>
          <span style={{ color: style.color }}>
            {style.symbol} {elementLabel(el, t)}
          </span>
          <span>{count.toFixed(1)}</span>
        </div>
        <div style={{ height: 6, borderRadius: 999, background: "rgba(255,255,255,0.06)" }}>
          <div
            style={{
              width: `${width}%`,
              height: "100%",
              borderRadius: 999,
              background: `linear-gradient(90deg, ${style.color}cc, ${style.color})`,
              boxShadow: `0 0 8px ${style.glow}`,
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div style={{ minWidth: 0 }}>
      <SectionIntro>{narratives?.elements?.intro}</SectionIntro>
      {narratives?.elements?.chemistryText && (
        <p style={{ fontSize: 12, color: acc.secondary, lineHeight: 1.65, marginBottom: 14 }}>
          {narratives.elements.chemistryText}
        </p>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
          gap: 16,
          minWidth: 0,
        }}
      >
        {["a", "b"].map((side) => {
          const profile = partners[side].elementProfile;
          const title = chartSideLabel(partners, side, t);
          return (
            <div key={side}>
              <div style={{ fontSize: 12, color: acc.secondary, marginBottom: 8 }}>{title}</div>
              {ELEMENTS.map((el) => renderBar(el, profile[el] || 0, 5))}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.08em", marginBottom: 8 }}>
          {t("couple_match_elements_combined_label")}
        </div>
        {ELEMENTS.map((el) => renderBar(el, combined[el] || 0, maxCombined))}
      </div>
      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.1em", marginBottom: 12 }}>
          {t("couple_match_elements_reading_title")}
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {(narratives?.elements?.profiles || []).map((profile) => {
            const style = elementColor(profile.element);
            const tierColor = elementTierColor(profile.tier, { secondary: acc.secondary });
            return (
              <div
                key={profile.slug}
                style={{
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.03)",
                  border: `1px solid ${style.color}33`,
                }}
              >
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: style.color, fontWeight: 500 }}>
                    {style.symbol} {elementLabel(profile.element, t)}
                  </span>
                  <span style={{ fontSize: 11, color: C.textMuted }}>{profile.count.toFixed(1)}</span>
                  <span
                    style={{
                      fontSize: 10,
                      color: tierColor,
                      padding: "2px 8px",
                      borderRadius: 999,
                      border: `1px solid ${tierColor}44`,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {profile.tierLabel}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 6 }}>
                  {chartSideLabel(partners, "a", t)} {profile.countA.toFixed(1)} · {chartSideLabel(partners, "b", t)}{" "}
                  {profile.countB.toFixed(1)}
                </div>
                <p style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.65, margin: 0 }}>{profile.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CoupleDaYunWindow({ window, acc, t, locale }) {
  return (
    <div
      style={{
        padding: "14px 16px",
        borderRadius: 10,
        background: "rgba(255,255,255,0.04)",
        border: window.isCurrent ? `1px solid ${acc.secondary}55` : `1px solid ${acc.primary}22`,
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 14, color: C.textPrimary, fontFamily: "'Cormorant Garamond', serif" }}>
          {window.startYear}–{window.endYear}
        </span>
        {window.isCurrent && (
          <span style={{ fontSize: 10, color: acc.secondary, letterSpacing: "0.08em" }}>
            {t("couple_match_dayun_current_badge")}
          </span>
        )}
        {window.isUpcoming && !window.isCurrent && (
          <span style={{ fontSize: 10, color: acc.primary, letterSpacing: "0.08em" }}>
            {t("couple_match_dayun_upcoming_badge")}
          </span>
        )}
        {window.branchLabel && (
          <span
            style={{
              fontSize: 10,
              color: acc.secondary,
              padding: "2px 8px",
              borderRadius: 999,
              border: `1px solid ${acc.secondary}44`,
            }}
          >
            {window.branchLabel}
          </span>
        )}
      </div>

      <div style={{ display: "grid", gap: 8, marginBottom: 10 }}>
        {window.cycleA && (
          <div style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.5 }}>
            <span style={{ color: acc.primary }}>{window.partnerA}:</span> {window.cycleA.ganZhi}{" "}
            {formatAnimal(window.cycleA.animal, locale)}
            <span style={{ color: C.textMuted }}>
              {" "}({resolveCoupleMatchLine(t, "couple_match_dayun_ages", { start: window.cycleA.startAge, end: window.cycleA.endAge })})
            </span>
          </div>
        )}
        {window.cycleB && (
          <div style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.5 }}>
            <span style={{ color: acc.primary }}>{window.partnerB}:</span> {window.cycleB.ganZhi}{" "}
            {formatAnimal(window.cycleB.animal, locale)}
            <span style={{ color: C.textMuted }}>
              {" "}({resolveCoupleMatchLine(t, "couple_match_dayun_ages", { start: window.cycleB.startAge, end: window.cycleB.endAge })})
            </span>
          </div>
        )}
      </div>

      {window.overlapText && (
        <p style={{ fontSize: 12, color: C.textPrimary, lineHeight: 1.65, margin: "0 0 8px", fontWeight: 500 }}>
          {window.overlapText}
        </p>
      )}
      {window.coupleInsight && (
        <p style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.65, margin: 0 }}>{window.coupleInsight}</p>
      )}
    </div>
  );
}

function DaYunPartnerCycles({ partnerBlock, acc, t, showAll, locale }) {
  const visible = showAll
    ? partnerBlock.cycles
    : partnerBlock.cycles.filter((c) => c.isCurrent || c.isUpcoming).slice(0, 3);

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, color: acc.secondary, marginBottom: 10 }}>
        {chartSideLabel(
          { [partnerBlock.side]: partnerBlock.partner },
          partnerBlock.side,
          t
        )}
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {visible.map((cycle) => (
          <div
            key={`${partnerBlock.side}-${cycle.index}`}
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "rgba(255,255,255,0.03)",
              border: cycle.isCurrent ? `1px solid ${acc.secondary}33` : `1px solid rgba(255,255,255,0.06)`,
            }}
          >
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 13, color: C.textPrimary }}>{cycle.ganZhi}</span>
              {cycle.isCurrent && (
                <span style={{ fontSize: 10, color: acc.secondary, letterSpacing: "0.08em" }}>
                  {t("couple_match_dayun_current_badge")}
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: C.textMuted }}>
              {cycle.startYear}–{cycle.endYear} · {resolveCoupleMatchLine(t, "couple_match_dayun_ages", { start: cycle.startAge, end: cycle.endAge })}
            </div>
              <div style={{ fontSize: 11, color: acc.primary, marginTop: 4 }}>
                {formatAnimal(cycle.animal, locale)}
              </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DaYunTimeline({ narratives, acc, t, locale = "en", isMobile = false }) {
  const [showAll, setShowAll] = useState(false);
  const [showIndividual, setShowIndividual] = useState(false);
  const dayun = narratives?.dayun;
  if (!dayun) return null;

  const visibleWindows = showAll
    ? dayun.coupleWindows
    : dayun.coupleWindows?.filter((w) => w.isCurrent || w.isUpcoming).slice(0, 3);

  return (
    <div style={{ display: "grid", gap: 20, minWidth: 0 }}>
      <SectionIntro>{dayun.intro}</SectionIntro>
      <p style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.65, margin: 0 }}>{dayun.decadeExplainer}</p>

      {visibleWindows?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: acc.primary, letterSpacing: "0.1em", marginBottom: 10 }}>
            {t("couple_match_dayun_windows_title")}
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            {visibleWindows.map((window) => (
              <CoupleDaYunWindow key={window.key} window={window} acc={acc} t={t} locale={locale} />
            ))}
          </div>
        </div>
      )}

      {dayun.coupleWindows?.length > 3 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          style={{
            background: "transparent",
            border: `1px solid ${acc.primary}44`,
            color: acc.primary,
            borderRadius: 8,
            padding: "8px 14px",
            fontSize: 12,
            cursor: "pointer",
            width: "fit-content",
          }}
        >
          {showAll ? t("couple_match_dayun_show_less") : t("couple_match_dayun_show_all")}
        </button>
      )}

      <button
        type="button"
        onClick={() => setShowIndividual((v) => !v)}
        style={{
          background: "transparent",
          border: `1px solid rgba(255,255,255,0.12)`,
          color: C.textMuted,
          borderRadius: 8,
          padding: "8px 14px",
          fontSize: 12,
          cursor: "pointer",
          width: "fit-content",
        }}
      >
        {showIndividual ? t("couple_match_dayun_hide_individual") : t("couple_match_dayun_show_individual")}
      </button>

      {showIndividual && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(min(100%, 240px), 1fr))", gap: 20 }}>
          <p style={{ fontSize: 11, color: C.textMuted, margin: 0, lineHeight: 1.6 }}>
            {t("couple_match_dayun_individual_note")}
          </p>
          {dayun.partners?.map((block) => (
            <DaYunPartnerCycles key={block.side} partnerBlock={block} acc={acc} t={t} showAll={showAll} locale={locale} />
          ))}
        </div>
      )}

      {dayun.footer && (
        <p style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.65, margin: 0 }}>{dayun.footer}</p>
      )}
    </div>
  );
}

export function JointYearsStrip({ years, acc, t, defaultYear, locale = "en" }) {
  const [selectedYear, setSelectedYear] = useState(defaultYear ?? years?.[0]?.year ?? null);
  const selected = years?.find((y) => y.year === selectedYear);

  const toneColor = {
    auspicious: acc.secondary,
    caution: "#ff7b9c",
    neutral: C.textMuted,
  };

  const legendItems = [
    { id: "a", label: t("couple_match_joint_tone_legend_auspicious"), color: acc.secondary },
    { id: "n", label: t("couple_match_joint_tone_legend_neutral"), color: C.textMuted },
    { id: "c", label: t("couple_match_joint_tone_legend_caution"), color: "#ff7b9c" },
  ];

  return (
    <div style={{ minWidth: 0, maxWidth: "100%" }}>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>{t("couple_match_joint_years_caption")}</div>
      <LegendRow items={legendItems} acc={acc} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, minWidth: 0, maxWidth: "100%" }}>
        {years.map((item) => {
          const active = item.year === selectedYear;
          return (
            <button
              key={item.year}
              type="button"
              onClick={() => setSelectedYear(item.year === selectedYear ? null : item.year)}
              style={{
                flex: "0 1 auto",
                padding: "8px 12px",
                borderRadius: 999,
                border: `1px solid ${toneColor[item.tone]}${active ? "" : "55"}`,
                color: toneColor[item.tone],
                fontSize: 12,
                background: active ? `${toneColor[item.tone]}28` : `${toneColor[item.tone]}14`,
                cursor: "pointer",
                font: "inherit",
              }}
            >
              {animalEmoji(item.annualAnimal, locale)} {item.year}
            </button>
          );
        })}
      </div>
      <YearDetailPanel yearItem={selected} acc={acc} onClose={() => setSelectedYear(null)} locale={locale} />
    </div>
  );
}

export function LifeTogetherCard({ lifeTogether, acc, t }) {
  if (!lifeTogether) return null;
  return (
    <div>
      {lifeTogether.headline && (
        <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: acc.secondary, margin: "0 0 12px" }}>
          {lifeTogether.headline}
        </h4>
      )}
      {lifeTogether.text && lifeTogether.text !== lifeTogether.headline && (
        <p style={{ color: C.textSecondary, fontSize: 14, lineHeight: 1.75, marginBottom: 16 }}>{lifeTogether.text}</p>
      )}
      <InsightList title={t("couple_match_life_strengths_title")} items={lifeTogether.strengths} accent={acc.secondary} />
      <InsightList title={t("couple_match_life_watchouts_title")} items={lifeTogether.watchouts} accent="#ff9ab0" />
      <InsightList title={t("couple_match_life_plan_title")} items={lifeTogether.plan} accent={acc.primary} />
    </div>
  );
}
